import { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from '../components/AppShell';
import { reportsApi, type AdminReportActivity } from '../services/api';

const PAGE_SIZE = 25;

const AdminReportsPage = () => {
  const [activity, setActivity] = useState<AdminReportActivity[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadActivity = useCallback(
    async (options?: { append?: boolean }) => {
      const append = options?.append ?? false;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await reportsApi.fetchAdminHistory({
          limit: PAGE_SIZE,
          before: append ? nextCursor ?? undefined : undefined
        });
        setNextCursor(response.has_more ? response.next_cursor ?? null : null);
        setActivity((prev) => (append ? [...prev, ...response.results] : response.results));
      } catch (err) {
        console.error(err);
        setError('Unable to load report activity. Please try again.');
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [nextCursor]
  );

  useEffect(() => {
    void loadActivity();
  }, [loadActivity]);

  const perUserStats = useMemo(() => {
    return activity.reduce<Record<string, number>>((acc, report) => {
      acc[report.user_username] = (acc[report.user_username] ?? 0) + 1;
      return acc;
    }, {});
  }, [activity]);

  const uniqueAnalysts = Object.keys(perUserStats).length;
  const topAnalysts = Object.entries(perUserStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  return (
    <AppShell title="Report activity ledger">
      {error && <p className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>}
      <section className="grid gap-6 lg:grid-cols-[340px,1fr]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/10 p-6">
            <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-400">Snapshot</h2>
            <dl className="mt-5 space-y-5 text-sm">
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <dt className="text-xs uppercase tracking-[0.3em] text-slate-500">Total reports loaded</dt>
                <dd className="text-2xl font-semibold text-brand-100">{loading ? '—' : activity.length}</dd>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <dt className="text-xs uppercase tracking-[0.3em] text-slate-500">Unique analysts</dt>
                <dd className="text-2xl font-semibold text-brand-100">{loading ? '—' : uniqueAnalysts}</dd>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <dt className="text-xs uppercase tracking-[0.3em] text-slate-500">Top activity</dt>
                <dd className="mt-3 space-y-2 text-slate-200">
                  {loading && <p className="text-xs text-slate-500">Loading…</p>}
                  {!loading && topAnalysts.length === 0 && <p className="text-xs text-slate-500">No reports yet.</p>}
                  {!loading &&
                    topAnalysts.map(([username, count]) => (
                      <div key={username} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                        <span className="font-medium text-slate-200">{username}</span>
                        <span className="text-brand-100">{count}</span>
                      </div>
                    ))}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5">
          <div className="overflow-auto">
            <table className="min-w-full divide-y divide-white/10 text-sm">
              <thead className="bg-black/80 text-xs uppercase tracking-[0.2em] text-slate-400">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left">
                    Timestamp
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    Analyst
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    Location
                  </th>
                  <th scope="col" className="px-4 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {loading && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-xs text-slate-500">
                      Loading report history…
                    </td>
                  </tr>
                )}
                {!loading && activity.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-xs text-slate-500">
                      No reports generated yet.
                    </td>
                  </tr>
                )}
                {activity.map((entry) => (
                  <tr key={entry.id} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-xs text-slate-400">{new Date(entry.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-white">{entry.user_username}</p>
                      <p className="text-xs text-slate-500">{entry.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-white">{entry.name}</p>
                      <p className="text-xs text-slate-500">{entry.formatted_address ?? `${entry.latitude.toFixed(3)}°, ${entry.longitude.toFixed(3)}°`}</p>
                    </td>
                    <td className="px-4 py-3 text-right text-xs">
                      <a
                        href={entry.public_url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-white/20 px-3 py-1 font-semibold text-slate-200 hover:border-brand-400 hover:text-brand-100"
                      >
                        Open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-white/10 p-4 text-right">
            <button
              type="button"
              onClick={() => void loadActivity({ append: true })}
              disabled={!nextCursor || loadingMore}
              className="rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-brand-400 hover:text-brand-100 disabled:cursor-not-allowed disabled:border-white/10 disabled:text-slate-600"
            >
              {loadingMore ? 'Loading…' : nextCursor ? 'Load older reports' : 'No older entries'}
            </button>
          </div>
        </div>
      </section>
    </AppShell>
  );
};

export default AdminReportsPage;
